import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSEO } from '@/hooks/useSEO';
import { ArrowRight, Check, Satellite, Mail } from 'lucide-react';
import BookDemoModal from '@/components/BookDemoModal';
import { useState } from 'react';

export interface LandingProps {
  slug: string;
  title: string;
  description: string;
  keywords: string;
  h1: string;
  kicker: string;
  intro: string;
  bullets: string[];
  sections: { h: string; p: string; list?: string[] }[];
  faqs: { q: string; a: string }[];
  ctaPrimary?: string;
  breadcrumbName: string;
}

const SocratLandingTemplate = (p: LandingProps) => {
  const pageUrl = `https://quantumbox.ai/sovereign-scout/${p.slug}`;
  const [demoOpen, setDemoOpen] = useState(false);

  useSEO({
    title: p.title,
    description: p.description,
    keywords: p.keywords,
    canonical: pageUrl,
    ogTitle: p.title,
    ogDescription: p.description,
  });

  useEffect(() => {
    const id = `socrat-landing-jsonld-${p.slug}`;
    const ld = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          '@id': `${pageUrl}#article`,
          headline: p.h1,
          description: p.description,
          inLanguage: 'en',
          url: pageUrl,
          author: { '@type': 'Organization', name: 'Quantumbox DeepTech Lab', url: 'https://quantumbox.ai' },
          publisher: {
            '@type': 'Organization',
            name: 'Quantumbox DeepTech Lab',
            logo: { '@type': 'ImageObject', url: 'https://quantumbox.ai/lovable-uploads/13ccca33-efca-4b7f-9ef1-50aafecd8c64.png' },
          },
          datePublished: '2026-05-15',
          dateModified: '2026-05-15',
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${pageUrl}#breadcrumb`,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://quantumbox.ai' },
            { '@type': 'ListItem', position: 2, name: 'Sovereign Scout', item: 'https://quantumbox.ai/sovereign-scout' },
            { '@type': 'ListItem', position: 3, name: p.breadcrumbName, item: pageUrl },
          ],
        },
        {
          '@type': 'FAQPage',
          '@id': `${pageUrl}#faq`,
          mainEntity: p.faqs.map(f => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
      ],
    };
    document.getElementById(id)?.remove();
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    el.text = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => { document.getElementById(id)?.remove(); };
  }, [p, pageUrl]);

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white">
      {/* Top nav */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0a0e1a]/90 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <Link to="/sovereign-scout" className="flex items-center gap-2 text-sm font-semibold text-amber-300">
            <Satellite className="h-5 w-5" /> SOCRAT · Sovereign Scout
          </Link>
          <button
            onClick={() => setDemoOpen(true)}
            className="rounded-md bg-amber-400 px-4 py-2 text-sm font-bold text-[#0a0e1a] hover:bg-amber-300 min-h-[44px]"
          >
            Book a Briefing →
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 px-4 py-16 md:py-24">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(251,191,36,0.12),transparent_60%)]" />
        <div className="container relative mx-auto max-w-4xl">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-amber-300">{p.kicker}</p>
          <h1 className="mb-6 text-4xl font-bold leading-tight md:text-6xl">{p.h1}</h1>
          <p className="mb-8 text-lg text-white/75 md:text-xl">{p.intro}</p>
          <ul className="mb-10 grid gap-3 sm:grid-cols-2">
            {p.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-white/85">
                <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-300" />
                {b}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setDemoOpen(true)}
              className="inline-flex items-center gap-2 rounded-md bg-amber-400 px-6 py-3 font-bold text-[#0a0e1a] hover:bg-amber-300 min-h-[48px]"
            >
              {p.ctaPrimary || 'Request a Sovereign Briefing'} <ArrowRight className="h-4 w-4" />
            </button>
            <Link
              to="/sovereign-scout"
              className="inline-flex items-center gap-2 rounded-md border border-white/20 px-6 py-3 font-semibold text-white hover:bg-white/5 min-h-[48px]"
            >
              See full SOCRAT platform
            </Link>
          </div>
        </div>
      </section>

      {/* Sections */}
      <main className="container mx-auto max-w-4xl px-4 py-16">
        {p.sections.map((s, i) => (
          <motion.section
            key={i}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-14"
          >
            <h2 className="mb-4 text-2xl font-bold text-amber-300 md:text-3xl">{s.h}</h2>
            <p className="mb-4 text-white/80 leading-relaxed">{s.p}</p>
            {s.list && (
              <ul className="space-y-2">
                {s.list.map((item, j) => (
                  <li key={j} className="flex items-start gap-2 text-white/80">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-300" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </motion.section>
        ))}

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="mb-6 text-2xl font-bold text-amber-300 md:text-3xl">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {p.faqs.map((f, i) => (
              <details key={i} className="rounded-lg border border-white/10 bg-white/5 p-5 group">
                <summary className="cursor-pointer font-semibold text-white list-none flex justify-between items-center">
                  {f.q}
                  <span className="text-amber-300 text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-white/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="rounded-2xl border border-amber-300/30 bg-gradient-to-br from-amber-500/10 to-transparent p-8 text-center">
          <h2 className="mb-3 text-2xl font-bold md:text-3xl">Stop funding drills. Start funding answers.</h2>
          <p className="mb-6 text-white/80">Brief SOCRAT on your concession or basin. Confidential. NDA available.</p>
          <button
            onClick={() => setDemoOpen(true)}
            className="inline-flex items-center gap-2 rounded-md bg-amber-400 px-8 py-4 font-bold text-[#0a0e1a] hover:bg-amber-300 min-h-[48px]"
          >
            Request a Sovereign Briefing <ArrowRight className="h-4 w-4" />
          </button>
        </section>
      </main>

      <footer className="border-t border-white/10 px-4 py-8 text-center text-sm text-white/60">
        <div className="container mx-auto flex flex-wrap justify-center gap-4">
          <Link to="/sovereign-scout" className="hover:text-amber-300">SOCRAT Platform</Link>
          <Link to="/about" className="hover:text-amber-300">About Quantumbox</Link>
          <Link to="/contact" className="hover:text-amber-300">Contact</Link>
          <a href="mailto:admin@quantumbox.ai" className="hover:text-amber-300 inline-flex items-center gap-1">
            <Mail className="h-3.5 w-3.5" /> admin@quantumbox.ai
          </a>
        </div>
        <p className="mt-4">© 2026 Sovereign Scout · Quantumbox DeepTech Lab</p>
      </footer>

      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
};

export default SocratLandingTemplate;
